import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Fixed speed (close to the top ticker's), so phone and desktop scroll alike.
const SPEED_PX_PER_S = 50;
// The message first rests this far in for HOLD_MS so it can be read, then moves.
const START_INSET_PX = 12;
const HOLD_MS = 2000;

// Info message that scrolls right-to-left in a loop like the top ticker; pauses
// on hover/press and stands still with "reduce motion". The track's
// padding-left: 100% makes its width the full travel distance (right edge → off
// the left), which sets the duration.
const ScrollingNotice = ({ children, sx = {} }) => {
  const theme = useTheme();
  const stripRef = useRef(null);
  const trackRef = useRef(null);
  const [timing, setTiming] = useState({ duration: 20, startPx: 0 });
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMoving(true), HOLD_MS);
    return () => clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    const track = trackRef.current;
    if (!strip || !track) return undefined;

    const update = () => {
      const distance = track.getBoundingClientRect().width;
      const stripWidth = strip.getBoundingClientRect().width;
      if (!distance) return;
      setTiming({ duration: distance / SPEED_PX_PER_S, startPx: Math.max(0, stripWidth - START_INSET_PX) });
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(strip);
    observer.observe(track);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={stripRef}
      role="note"
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: 26,
        overflow: 'hidden',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
        bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
        '&:hover .scrolling-notice-track, &:active .scrolling-notice-track': {
          animationPlayState: 'paused',
        },
        ...sx,
      }}
    >
      <Box
        ref={trackRef}
        className="scrolling-notice-track"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
          pl: '100%',
          whiteSpace: 'nowrap',
          fontSize: '0.8125rem',
          color: 'text.primary',
          willChange: 'transform',
          // The negative delay starts the loop exactly where the resting
          // transform left the message, so the switch is seamless.
          ...(moving
            ? { animation: `scrolling-notice ${timing.duration}s linear ${-timing.startPx / SPEED_PX_PER_S}s infinite` }
            : { transform: `translateX(${-timing.startPx}px)` }),
          '@keyframes scrolling-notice': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-100%)' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            transform: 'none',
            pl: 1.5,
            pr: 1.5,
            py: 0.5,
            flexShrink: 1,
            whiteSpace: 'normal',
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', flexShrink: 0 }} />
        <span>{children}</span>
      </Box>
    </Box>
  );
};

export default ScrollingNotice;
